import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import {
  NotificationType,
  ShiveNotificationService,
} from '../../services/controls/shive-notification.service';
import * as fromInvoiceActions from './invoice-document.actions';
import { map, take } from 'rxjs/operators';
import { InvoiceDocumentDetailModel } from '../../models/invoice/invoice-document-detail.model';
import { InvoiceDocumentOverviewModel } from '../../models/invoice/invoice-document-overview.model';

@Injectable({
  providedIn: 'root',
})
export class InvoiceDocumentHttpService {
  private readonly store = inject(Store);
  private readonly shiveNotificationService = inject(ShiveNotificationService);
  private readonly actions$ = inject(Actions);

  constructor() {
    this.handleHttpCallbacks();
  }

  // /** Invoice Document Overview **/
  // Fetch list of generated invoices
  public getInvoiceDocumentOverviewModels(): Observable<InvoiceDocumentOverviewModel[]> {
    this.store.dispatch(fromInvoiceActions.fetchInvoiceDocumentOverview());

    return this.actions$.pipe(
      ofType(fromInvoiceActions.setInvoiceDocumentOverview),
      map((result) => result.payload),
      take(1),
    );
  }

  // Delete an existing invoice
  public deleteInvoiceDocumentOverviewModel(
    toBeDeleted: InvoiceDocumentOverviewModel,
  ): Observable<InvoiceDocumentOverviewModel> {
    this.store.dispatch(fromInvoiceActions.deleteInvoiceDocumentOverview({ payload: toBeDeleted }));

    return this.actions$.pipe(
      ofType(fromInvoiceActions.invoiceDocumentOverviewDeleted),
      map((result) => result.payload),
      take(1),
    );
  }

  // /** Invoice Document Detail **/
  // Fetch all invoices or templates
  public fetchAllInvoiceDocuments(onlyTemplates = false): Observable<InvoiceDocumentDetailModel[]> {
    this.store.dispatch(fromInvoiceActions.fetchInvoiceDocuments({ payload: onlyTemplates }));

    return this.actions$.pipe(
      ofType(fromInvoiceActions.setInvoiceDocuments),
      map((result) => result.payload),
    );
  }

  // Fetch single invoice
  public fetchSingleInvoiceDocument(id: number): Observable<InvoiceDocumentDetailModel> {
    this.store.dispatch(fromInvoiceActions.fetchSingleInvoiceDocument({ payload: id }));

    return this.actions$.pipe(
      ofType(fromInvoiceActions.setSingleInvoiceDocument),
      map((result) => result.payload),
    );
  }

  // Generate a new invoice
  public generateInvoiceDocument(
    payload: InvoiceDocumentDetailModel,
  ): Observable<InvoiceDocumentDetailModel> {
    this.store.dispatch(fromInvoiceActions.generateInvoiceDocument({ payload }));

    return this.actions$.pipe(
      ofType(fromInvoiceActions.invoiceDocumentGenerated),
      map((result) => result.payload),
      take(1),
    );
  }

  public updateInvoiceDocument(
    invoiceDocumentDetailModel: InvoiceDocumentDetailModel,
  ): Observable<InvoiceDocumentDetailModel> {
    this.store.dispatch(
      fromInvoiceActions.updateInvoiceDocument({
        payload: invoiceDocumentDetailModel,
      }),
    );

    return this.actions$.pipe(ofType(fromInvoiceActions.invoiceDocumentUpdated));
  }

  public deleteInvoiceDocument(
    invoiceDocumentDetailModel: InvoiceDocumentDetailModel,
  ): Observable<InvoiceDocumentDetailModel> {
    this.store.dispatch(
      fromInvoiceActions.deleteInvoiceDocument({
        payload: invoiceDocumentDetailModel,
      }),
    );

    return this.actions$.pipe(ofType(fromInvoiceActions.invoiceDocumentUpdated));
  }

  // Success toast on create/delete
  private handleHttpCallbacks(): void {
    this.actions$
      .pipe(
        ofType(
          fromInvoiceActions.invoiceDocumentGenerated,
          fromInvoiceActions.invoiceDocumentUpdated,
          fromInvoiceActions.invoiceDocumentOverviewDeleted,
        ),
      )
      .subscribe(() => {
        this.shiveNotificationService.show(NotificationType.Success);
      });
  }
}
